import axios, { AxiosResponse } from 'axios';
import { useState } from 'react';
import { createMultiPartRequest, createRequest } from '../helpers/api_utils';
import {
  AssessmentSettings,
  AssessmentTemplateCreate, AssessmentTemplateRequest, AssessmentsSearchObject,
  BasicInfoInputs,
  CandidateStatus, FilterState,
  InviteUserRequest,
  PartnerQueryFilter, PreScreenAnswers,
  PreScreenQuestion,
  QueryFilter,
  QuestionSortOrder,
} from '../types/assessment_types';
import { JobOpeningRequest } from '../types/JobOpenings';
import { apiUrl } from './api_url';

const apiEndpoint = apiUrl + '/assessments';
const qnaApiEndpoint = apiEndpoint + '/qna';
const settingsApiEndpoint = apiEndpoint + '/settings';
const assessmentUserApiEndpoint = apiEndpoint + '/user';
export const assessmentFlowApiEndpoint = apiUrl + '/webrtc';
const candidateStatusEndpoint = assessmentUserApiEndpoint + '/candidate_status';

export function createAssessment(inputs: BasicInfoInputs) {
  return fetch(createRequest(apiEndpoint, 'POST', 'application/json', true, inputs))
    .then(response => {
      return response.json();
    });
}

export function updateAssessment(inputs: BasicInfoInputs, assessmentId: string) {
  return fetch(createRequest(apiEndpoint + `/update?id=${encodeURIComponent(assessmentId)}`, 'PUT', 'application/json', true, inputs))
    .then(response => {
      return response.json();
    });
}

export function getAssessment(id: string) {
  return fetch(createRequest(apiEndpoint + `?id=${encodeURIComponent(id)}`, 'GET', 'application/json', true))
    .then(response => {
      return response.json();
    });
}

export function deleteAssessment(id: string) {
  return fetch(createRequest(apiEndpoint + `/${encodeURIComponent(id)}`, 'DELETE', 'application/json', true));
}

export function getAssessmentList() {
  return fetch(createRequest(apiEndpoint + '/list', 'GET', 'application/json', true))
    .then(response => {
      return response.json();
    });
}

export function searchAssessmentList(searchObject: AssessmentsSearchObject) {
  return fetch(createRequest(apiEndpoint + '/search', 'POST', 'application/json', true, searchObject))
    .then(response => {
      return response.json();
    });
}

export function getAssessmentQuestions(id: string) {
  return fetch(createRequest(apiEndpoint + `/questions/${encodeURIComponent(id)}`, 'GET', 'application/json', true))
    .then(response => {
      return response.json();
    });
}

export function addQuestionToAssessment(sortingOrder: string, questionId: string, assessmentId: string) {
  const body = {
    sort_order: sortingOrder,
    question_answer_id: questionId,
    assessment_id: assessmentId,
  };
  return fetch(createRequest(qnaApiEndpoint, 'POST', 'application/json', true, body))
    .then(response => {
      return response.json();
    });
}

export function updateQuestionOrderInAssessment(sortedQuestionOrder: QuestionSortOrder[]) {
  return fetch(createRequest(qnaApiEndpoint + '/sort_order', 'PUT', 'application/json', true, sortedQuestionOrder));
}

export function deleteQuestionFromAssessment(id: string) {
  return fetch(createRequest(qnaApiEndpoint + `/delete/${encodeURIComponent(id)}`, 'DELETE', 'application/json', true));
}

export function deleteAllQuestionFromAssessment(body: string[]) {
  return fetch(createRequest(qnaApiEndpoint + '/delete', 'DELETE', 'application/json', true, body));
}

export function updateAssessmentStatus(id: string, status: boolean) {
  const body = {
    assessmentId: id,
    isActive: status,
  };
  return fetch(createRequest(apiEndpoint + '/status', 'PUT', 'application/json', true, body));
}

export function createAssessmentSettings(assessmentSettings: AssessmentSettings) {
  const body = {
    assessment_id: assessmentSettings.assessmentId,
    show_score: assessmentSettings.showScore,
    shuffle_questions: assessmentSettings.shuffleQuestions,
    enable_copy: assessmentSettings.enableCopy,
    attempt_count: parseInt(assessmentSettings.attemptCount),
    custom_message: assessmentSettings.customMessage,
    blur_video: assessmentSettings.blurVideo,
    threshold_percent: parseInt(assessmentSettings.thresholdPercent || '0'),
    unsuccessful_email: assessmentSettings.unsuccessfulEmail,
  };
  return fetch(createRequest(settingsApiEndpoint, 'POST', 'application/json', true, body));
}

export function updateAssessmentSettings(id: string, assessmentSettings: AssessmentSettings) {
  const body = {
    assessment_id: id,
    show_score: assessmentSettings.showScore,
    shuffle_questions: assessmentSettings.shuffleQuestions,
    enable_copy: assessmentSettings.enableCopy,
    attempt_count: parseInt(assessmentSettings.attemptCount),
    custom_message: assessmentSettings.customMessage,
    blur_video: assessmentSettings.blurVideo,
    threshold_percent: parseInt(assessmentSettings.thresholdPercent || '0'),
    unsuccessful_email: assessmentSettings.unsuccessfulEmail,
  };
  return fetch(createRequest(settingsApiEndpoint + '/update', 'PUT', 'application/json', true, body));
}

export function getAssessmentSettings(id: string) {
  return fetch(createRequest(settingsApiEndpoint + `?id=${encodeURIComponent(id)}`, 'GET', 'application/json', true))
    .then(response => {
      return response.json();
    });
}

export function inviteUserToAssessment(inviteUserRequest: InviteUserRequest) {
  return fetch(createRequest(apiEndpoint + '/invite', 'POST', 'application/json', true, inviteUserRequest))
    .then(response => {
      return response.json();
    });
}

export function getAssessmentUsers(assessmentId: string) {
  return fetch(createRequest(assessmentUserApiEndpoint + `/get/assessment_id?assessment_id=${assessmentId}`, 'GET', 'application/json', true))
    .then(response => {
      return response.json();
    });
}

export function getAssessmentsByUserId() {
  return fetch(createRequest(assessmentUserApiEndpoint + '/get/user_id', 'GET', 'application/json', true))
    .then(response => {
      return response.json();
    });
}

export function deleteAssessmentUser(assessmentUserId: string) {
  return fetch(createRequest(assessmentUserApiEndpoint + `/${assessmentUserId}`, 'DELETE', 'application/json', true));
}

export function updateAssessmentUserStatus(assessmentUserId: string, enabled: boolean) {
  const body = {
    assessmentUserId: assessmentUserId,
    enabled: enabled,
  };
  return fetch(createRequest(assessmentUserApiEndpoint + '/status', 'PUT', 'application/json', true, body));
}

export function startPracticeAssessment() {
  return fetch(createRequest(assessmentFlowApiEndpoint + '/practice-assessment', 'GET', 'application/json', true))
    .then(response => {
      return response.json();
    });
}

export function assessmentPing(userAssessmentQuestionId: string) {
  return fetch(createRequest(assessmentFlowApiEndpoint + `/ping?user_assessment_question_answer_id=${userAssessmentQuestionId}`, 'GET', 'application/json', true))
    .then(response => {
      return response.json();
    });
}

export function navigateNextQuestion(userAssessmentId: string) {
  return fetch(createRequest(assessmentFlowApiEndpoint + `/next?user_assessment_id=${userAssessmentId}`, 'GET', 'application/json', true))
    .then(response => {
      return response.json();
    });
}

export function uploadQuestionAnswer(userAssessmentQnaId: string, timeExpired: string, textAnswer?: string, videoFile?: File) {
  let formData = new FormData();
  formData.append('user_assessment_qna_id', userAssessmentQnaId);
  formData.append('time_expired_str', timeExpired.toString());
  if (textAnswer && videoFile) {
    formData.append('user_answer', textAnswer);
    formData.append('file', videoFile);
  }

  const requestOptions = {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
    },
    body: formData,
  };

  return fetch(assessmentFlowApiEndpoint + '/upload', requestOptions);
}

export function getDownloadLink(userAssessmentQnaId: string) {
  return fetch(createRequest(assessmentFlowApiEndpoint + `/download?user_assessment_qna_id=${userAssessmentQnaId}`, 'POST', 'application/json', true))
    .then(response => {
      return response.json();
    });
}

export function getAssessmentResultList(assessmentId: string) {
  return fetch(createRequest(apiEndpoint + `/user/${assessmentId}`, 'GET', 'application/json', true))
    .then(response => {
      return response.json();
    });
}
export function getAssessmentResultsOverview(assessmentId: string) {
  return fetch(
    createRequest(
      apiEndpoint + `/user/${assessmentId}/overview`,
      'GET',
      'application/json',
      true,
    ),
  ).then((response) => {
    return response.json();
  });
}
export function getDetailedAssessmentResults(userAssessmentId: string) {
  return fetch(createRequest(apiEndpoint + `/user/detailed/${userAssessmentId}`, 'GET', 'application/json', true))
    .then(response => {
      return response.json();
    });
}

export function toggleAttemptMarkedStatus(assessmentUserId: string, marked: boolean) {
  const body = {
    assessment_user_id: assessmentUserId,
    marked: marked,
  };
  return fetch(createRequest(apiEndpoint + '/user/marked', 'PUT', 'application/json', true, body));
}

export function updateAnswerNotesAndStatus(assessmentUserQnaId: string, isCorrect: boolean, personalNotes: string) {
  const body = {
    assessmentUserQnaId: assessmentUserQnaId,
    isCorrect: isCorrect,
    personalNotes: personalNotes || '',
  };
  return fetch(createRequest(apiEndpoint + '/user/marked/QNA', 'PUT', 'application/json', true, body));
}

export function getManagerSummary(managerId: string) {
  return fetch(createRequest(apiEndpoint + `/manager-dashboard-summary?user_id=${managerId}`, 'GET', 'application/json', true))
    .then(response => {
      return response.json();
    });
}

export function setAssessmentEmployeeQuery(query: QueryFilter) {
  return fetch(createRequest(apiEndpoint + '/query', 'POST', 'application/json', true, query))
    .then(response => {
      return response.json();
    });
}

export function setAssessmentEmployeePartnerQuery(query: PartnerQueryFilter) {
  return fetch(createRequest(apiEndpoint + '/partner/query', 'POST', 'application/json', true, query));
}

export function setCandidateStatus(requestBody: { assessmentId: string, candidateStatus: { status: string, userId: string }[] }) {
  return fetch(createRequest(candidateStatusEndpoint, 'POST', 'application/json', true, requestBody));
}

// Individual assessment user update - similar to editJobApplicant
export function updateAssessmentUserCandidateStatus(
  assessmentId: string,
  assessmentUserId: string,
  candidateStatus: string,
) {
  const body = {
    candidateStatus: candidateStatus,
  };
  return fetch(
    createRequest(
      apiEndpoint + `/${assessmentId}/user/${assessmentUserId}/candidate-status`,
      'PUT',
      'application/json',
      true,
      body,
    ),
  );
}

export function sendCandidateEmail(requestBody: { assessmentId: string, candidateStatus: CandidateStatus}) {
  return fetch(createRequest(candidateStatusEndpoint + '/email', 'POST', 'application/json', true, requestBody));
}

export function exportCandidateCSV(requestBody: { assessmentId: string, candidateStatus: CandidateStatus}) {
  return fetch(createRequest(candidateStatusEndpoint + '/csv', 'POST', 'application/json', true, requestBody));
}

export function setPreScreenQuestions(assessmentId: string, preScreenQuestions: PreScreenQuestion[]) {
  const requestBody = {
    preScreenQuestions: preScreenQuestions,
  };
  return fetch(createRequest(apiEndpoint + `/${assessmentId}/pre-screen`, 'POST', 'application/json', true, requestBody));
}

export function submitPreScreenAnswers(assessmentId: string, userAssessmentId: string, preScreenAnswers: PreScreenAnswers[]) {
  const requestBody = {
    preScreenAnswers: preScreenAnswers,
  };
  return fetch(createRequest(apiEndpoint + `/${assessmentId}/pre-screen/${userAssessmentId}/answers`, 'POST', 'application/json', true, requestBody));
}

export function filterPreScreenAnswers(assessmentId: string, preScreenFilters: FilterState[]) {
  const requestBody = {
    filterPreScreen: preScreenFilters,
  };
  return fetch(createRequest(apiEndpoint + `/${assessmentId}/pre-screen/filter`, 'POST', 'application/json', true, requestBody));
}

export function getAssessmentUsersResult(
  assessmentId: string,
  candidateStatus?: string,
  sort?: string,
  page: number = 1,
  limit: number = 20,
  preScreenFilters: FilterState[] = [],
) {
  const queryParams = new URLSearchParams();

  if (candidateStatus) {
    queryParams.append('candidate_status', candidateStatus);
  }

  if (sort) {
    queryParams.append('sort', sort);
  }

  queryParams.append('page', page.toString());
  queryParams.append('limit', limit.toString());

  const requestBody = {
    filterPreScreen: preScreenFilters,
  };

  const url = `${apiEndpoint}/${assessmentId}/users-result${
    queryParams.toString() ? '?' + queryParams.toString() : ''
  }`;
  return fetch(
    createRequest(url, 'POST', 'application/json', true, requestBody),
  );
}

export function resultsPreScreen(userAssessmentId: string) {
  return fetch(createRequest(apiEndpoint + `/pre-screen/${userAssessmentId}/results`, 'GET', 'application/json', true));
}

export async function getAssessmentTemplateList(
  body: AssessmentTemplateRequest,
  page: number = 1,
  limit: number = 10,
) {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  return fetch(createRequest(apiEndpoint + `/templates/list?${queryParams.toString()}`, 'POST', 'application/json', true, body));
}

export async function createAssessmentTemplate(body: AssessmentTemplateCreate) {
  return fetch(createRequest(apiEndpoint + '/templates', 'POST', 'application/json', true, body));
}

export function getAssessmentTemplate(templateId: string) {
  return fetch(
    createRequest(
      apiEndpoint + `/templates/${templateId}`,
      'GET',
      'application/json',
      true,
    ),
  );
}

export function deleteAssessmentTemplate(templateId: string) {
  return fetch(
    createRequest(
      apiEndpoint + `/templates/${templateId}`,
      'DELETE',
      'application/json',
      true,
    ),
  );
}

export async function startAssessment(
  userAssessmentId: string,
) {
  return fetch(
    createRequest(
      assessmentFlowApiEndpoint + `/start-assessment?user_assessment_id=${userAssessmentId}`,
      'GET',
      'application/json',
      true,
    ),
  );
}

export async function assessmentStages(
  assessmentId: string,
) {
  return fetch(
    createRequest(
      apiEndpoint + `/${assessmentId}/start`,
      'GET',
      'application/json',
      true,
    ),
  );
}

export function uploadQuestionAnswerv(userAssessmentQnaId: string, timeExpired: string, textAnswer?: string, videoFile?: File) {
  let formData = new FormData();
  formData.append('user_assessment_qna_id', userAssessmentQnaId);
  formData.append('time_expired_str', timeExpired.toString());
  if (textAnswer && videoFile) {
    formData.append('user_answer', textAnswer);
    formData.append('file', videoFile);
  }

  return fetch(createMultiPartRequest(assessmentFlowApiEndpoint + '/upload', 'POST', true, formData));

}

export const useUploadAnswer = () => {
  const url =  apiEndpoint + '/answer/audio';
  const [uploadStatus, setUploadStatus] = useState<'initial' | 'loading' | 'success' | 'error'>('initial');
  const [progress, setProgress] = useState(0);

  const uploadForm = async (instanceId: string, questionId: string, answer: string, audioBlob: Blob) => {
    let formData = new FormData();
    formData.append('user_question_assessment_id', instanceId);
    formData.append('question_id', questionId);
    formData.append('answer', answer);
    const audioFile = new File([audioBlob], 'answer.webm', { type: 'audio/webm' });
    formData.append('file', audioFile);
    setUploadStatus('loading');

    try {
      const response: AxiosResponse = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          const percent = Math.min(
            (progressEvent.loaded / progressEvent.total) * 100,
            92,
          );
          setProgress(percent);
        },
      });

      setProgress(100);
      setUploadStatus('success');

      return response.data;
    } catch (error) {
      setUploadStatus('error');
      throw error;
    } finally {
      setTimeout(() => setUploadStatus('initial'), 300);
    }
  };

  return { uploadForm, uploadStatus, progress };
};

export async function getPing(
  userAssessmentId: string,
) {
  return fetch(
    createRequest(
      assessmentFlowApiEndpoint + `/${userAssessmentId}/ping`,
      'GET',
      'application/json',
      true,
    ),
  );
}
